/**
 * Settings → Team: who can sign in. With accounts enabled (Supabase) this lists
 * them, adds colleagues, deactivates or reactivates them, and changes your own
 * password. Without accounts everyone shares the dashboard key.
 */
import { useCallback, useEffect, useState } from 'react';

import { MIN_PASSWORD_LENGTH } from '../lib/password.js';

async function request(apiBase, path, { method = 'GET', body } = {}) {
  const response = await fetch(`${apiBase}/api/accounts${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || data.error || `Request failed (${response.status})`);
  return data;
}

export default function TeamTab({ apiBase, currentUser, accountsEnabled, theme, onToggleTheme }) {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [invite, setInvite] = useState({ name: '', email: '', password: '' });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setUsers(await request(apiBase, ''));
    } catch (err) {
      setError(err.message);
    }
  }, [apiBase]);

  useEffect(() => {
    if (accountsEnabled) load();
  }, [accountsEnabled, load]);

  async function run(action, success) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      if (success) setNotice(success);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const addColleague = (event) => {
    event.preventDefault();
    run(async () => {
      await request(apiBase, '', { method: 'POST', body: invite });
      setInvite({ name: '', email: '', password: '' });
      await load();
    }, 'Account created. Share the password with them privately; they can change it after signing in.');
  };

  const changePassword = (event) => {
    event.preventDefault();
    run(async () => {
      await request(apiBase, '/password', { method: 'POST', body: passwords });
      setPasswords({ currentPassword: '', newPassword: '' });
    }, 'Password changed. Every other browser was signed out.');
  };

  const toggle = (user) => run(async () => {
    await request(apiBase, `/${user.id}/${user.isActive ? 'deactivate' : 'activate'}`, { method: 'POST' });
    await load();
  });

  return (
    <div className="settings__section">
      <h3 className="settings__section-title">Team &amp; security</h3>

      <div className="settings__row">
        <span>Theme</span>
        <button type="button" className="btn" onClick={onToggleTheme}>Switch to {theme === 'dark' ? 'light' : 'dark'} mode</button>
      </div>

      {!accountsEnabled ? (
        <p className="settings__hint settings__hint--info">
          Everyone signs in with the shared dashboard key. Individual accounts need Supabase (set <code>SUPABASE_URL</code> and{' '}
          <code>SUPABASE_SERVICE_KEY</code>) — then each person signs in with their own email and password.
        </p>
      ) : (
        <>
          {currentUser && <p className="settings__hint">Signed in as <strong>{currentUser.email}</strong>.</p>}

          <table className="sheet__table">
            <thead><tr><th>Person</th><th>Status</th><th>Last sign-in</th><th></th></tr></thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.name ? `${user.name} · ` : ''}{user.email}</td>
                  <td>{user.isActive ? 'Active' : 'Deactivated'}</td>
                  <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}</td>
                  <td>
                    {user.id !== currentUser?.id && (
                      <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => toggle(user)}>
                        {user.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <form className="settings__add-row" onSubmit={addColleague}>
            <input className="input" placeholder="Name" aria-label="Name" maxLength={80} value={invite.name}
              onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            <input className="input" type="email" required placeholder="Email" aria-label="Email" autoComplete="off" value={invite.email}
              onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            <input className="input" type="password" required minLength={MIN_PASSWORD_LENGTH} placeholder={`Password (${MIN_PASSWORD_LENGTH}+ characters)`}
              aria-label="Password for the new account" autoComplete="new-password" value={invite.password}
              onChange={(e) => setInvite({ ...invite, password: e.target.value })} />
            <button type="submit" className="btn btn--primary" disabled={busy}>Add person</button>
          </form>

          <h3 className="settings__section-title">Change my password</h3>
          <form className="settings__add-row" onSubmit={changePassword}>
            <input className="input" type="password" required placeholder="Current password" aria-label="Current password"
              autoComplete="current-password" value={passwords.currentPassword}
              onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })} />
            <input className="input" type="password" required minLength={MIN_PASSWORD_LENGTH} placeholder="New password" aria-label="New password"
              autoComplete="new-password" value={passwords.newPassword}
              onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })} />
            <button type="submit" className="btn" disabled={busy}>Change password</button>
          </form>
        </>
      )}

      {error && <p className="settings__hint settings__hint--warn" role="alert">{error}</p>}
      {notice && <p className="settings__hint" role="status">{notice}</p>}
    </div>
  );
}
